import {
  AloftPrice,
  SalesComparisonApproachType,
  applyRoundingAndThresholds,
} from '@aloft/toolkit-shared';
import { createSelector } from '@reduxjs/toolkit';

import {
  selectAdjustmentSettings,
  selectCompSequence,
  selectCompTypes,
  selectSalesComparisonApproach,
} from '../orderSelectors.ts';
import { selectImprovementsEntities } from '../propertyDataSelectors/improvementsSelectors.ts';
import { selectManualAdjustmentsEntities } from '../propertyDataSelectors/manualAdjustmentsSelectors.ts';
import { selectSubjectUnitRateForFeature } from '../propertyDataSelectors/unitRateSelectors.ts';
import { createPriceSelector } from '../utils/createPriceSelector.ts';
import { calculateUnitRateAdjustment } from './utils/calculateUnitRateAdjustment.ts';
import { getColorFromAdjustment } from './utils/getColorFromAdjustment.ts';
import { getStringValueFromAdjustment } from './utils/getStringValueFromAdjustment.ts';

const calculateADUHalfBathsAdj = (
  improvements: ReturnType<typeof selectImprovementsEntities>,
  compID: string,
  compTypes: ReturnType<typeof selectCompTypes>,
  salesComparisonApproach: SalesComparisonApproachType,
  unitRate?: AloftPrice,
) => {
  const compType = compTypes[compID];
  let subjectImprovements = improvements.subject;

  if (compType === 'arv') subjectImprovements = improvements.subjectArv;
  const compImprovements = improvements[compID];

  if (!subjectImprovements || !compImprovements) return undefined;

  const { adu } = subjectImprovements;
  const { adu: compADU } = compImprovements;

  if (salesComparisonApproach === 'unit_rate') {
    return calculateUnitRateAdjustment({
      unitRate,
      subjectUnitAmount: adu?.halfBaths,
      compUnitAmount: compADU?.halfBaths,
    });
  }

  // Depreciated cost adjustments didn't account for ADU half baths
  return undefined;
};

export const selectADUHalfBathsAdj = createPriceSelector(
  [
    selectImprovementsEntities,
    (state, id: string) => id,
    selectCompTypes,
    selectSalesComparisonApproach,
    (state) => selectSubjectUnitRateForFeature(state, 'aduHalfBaths'),
  ],
  calculateADUHalfBathsAdj,
);

export const selectADUHalfBathsAdjs = createSelector(
  [
    selectImprovementsEntities,
    selectCompSequence,
    selectCompTypes,
    selectSalesComparisonApproach,
    (state) => selectSubjectUnitRateForFeature(state, 'aduHalfBaths'),
  ],
  (improvements, compSequence, compTypes, salesComparisonApproach, unitRate) =>
    compSequence.reduce((acc: Record<string, AloftPrice>, propID) => {
      const adj = calculateADUHalfBathsAdj(
        improvements,
        propID,
        compTypes,
        salesComparisonApproach,
        unitRate,
      );

      if (adj) acc[propID] = adj;
      return acc;
    }, {}),
);

const selectAppliedADUHalfBathsAdj = createPriceSelector(
  [selectADUHalfBathsAdj, selectAdjustmentSettings],
  (adj, adjSettings) => applyRoundingAndThresholds(adj, 'aduHalfBaths', adjSettings),
);

export const selectAppliedADUHalfBathsAdjDisplay = createSelector(
  [selectAppliedADUHalfBathsAdj],
  (adj) => {
    if (!adj) return undefined;

    return {
      value: getStringValueFromAdjustment(adj),
      color: getColorFromAdjustment(adj),
    };
  },
);

export const selectAppliedADUHalfBathsAdjsDisplay = createSelector(
  [selectADUHalfBathsAdjs, selectAdjustmentSettings, selectManualAdjustmentsEntities],
  (adjs, adjSettings, manualAdjustmentsEntities) =>
    Object.keys(adjs).reduce((acc: Record<string, { value: string; color: string }>, id) => {
      const adj = adjs[id];
      const manualAdjustment = manualAdjustmentsEntities[id]?.aduHalfBaths;

      if (manualAdjustment) {
        const manualAdjustmentPrice = AloftPrice.schema.parse(manualAdjustment);
        acc[id] = {
          value: `${getStringValueFromAdjustment(manualAdjustmentPrice)}*`,
          color: getColorFromAdjustment(manualAdjustmentPrice),
        };
      } else if (adj) {
        acc[id] = {
          value: getStringValueFromAdjustment(
            applyRoundingAndThresholds(adj, 'aduHalfBaths', adjSettings),
          ),
          color: getColorFromAdjustment(adj),
        };
      }

      return acc;
    }, {}),
);
